<script lang="ts" setup>
import { useChallengeV2 } from 'vue-recaptcha'
import { z } from 'zod'

const props = defineProps({
  currentView: {
    default: 'login',
    type: String as PropType<AuthBoxCurrentView>,
  },
  size: {
    default: 'sm',
    type: String as PropType<AuthBoxSize>,
  },
  waitForEmailVerification: {
    default: true,
    type: Boolean,
  },
})

const emit = defineEmits<{
  authenticated: []
}>()

const api = useApi()
const { toggleModal } = useOverlays()
const { createEvent } = useAnalytics()
const { createToast } = useToasts()
const { locale, t } = useI18n()
const authStore = useAuthStore()
const config = useRuntimeConfig()
const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

const currentView = defineModel<AuthBoxCurrentView>('currentView', { required: true })
const loggingInWithEmail = ref(false)
const showPasswordField = ref(false)
const waitingForEmailVerification = ref(false)
const resending = ref(false)

const {
  execute: executeRecaptcha,
  onError: onErrorRecaptcha,
  onVerify: onVerifyRecaptcha,
  reset: resetRecaptcha,
  root: recaptchaRef,
} = useChallengeV2({ options: { size: 'invisible' } })

// Support auto-login in development mode
const { public: { devLoginEmail, devLoginPassword } } = useRuntimeConfig()

// Form Validation
const form = reactive({
  email: config.public.nuxtEnv === 'production' ? '' : devLoginEmail || 'heyhey@gmail.com',
  password: config.public.nuxtEnv === 'production' ? '' : devLoginPassword || 'heyhey',
  recaptchaToken: '',
})

const validation = z.object({
  email: z.string().email(t('validation.email')),
  password: z.string().refine(
    (password) => {
      if (currentView.value === 'login' && showPasswordField.value) {
        return password.length >= 6 && password.length <= 255
      }

      return true
    },
    {
      message: t('validation.between', {
        max: 255,
        min: 6,
      }),
    },
  ),
})

const verifyEmailActions = computed((): UAlertAction[] => {
  return [
    {
      click: onOpenGmail,
      color: 'gray',
      icon: getIcon('gmail'),
      label: t('openGmail'),
      size: 'sm',
      variant: 'solid',
    },
    {
      click: onResendVerificationEmail,
      color: 'gray',
      icon: getIcon('emailResend'),
      label: t('resendEmail'),
      loading: resending.value,
      size: 'sm',
      variant: 'solid',
    },
    {
      click: async () => {
        await authStore.logout()

        currentView.value = 'login'
        waitingForEmailVerification.value = false
        resending.value = false
        loggingInWithEmail.value = false

        toggleModal({
          opened: false,
          type: 'auth',
        })
      },
      color: 'gray',
      icon: getIcon('logout'),
      label: t('logout'),
      size: 'sm',
    },
  ]
})

onMounted(
  async () => {
    if (route?.query?.email) {
      form.email = route?.query.email as string
    }

    if (currentView.value === 'verifyEmail') {
      await waitForEmailVerification()
    }
  },
)

function getRecaptchaToken(): Promise<string> {
  return new Promise((resolve, reject) => {
    onVerifyRecaptcha(token => resolve(token))
    onErrorRecaptcha(error => reject(error))
    executeRecaptcha()
  })
}

async function waitForEmailVerification() {
  const userStore = useUserStore()

  let tries = 0
  let isEmailVerified = !!userStore?.user?.emailVerified

  // Early return if email is already verified; skipping the waiting process
  if (isEmailVerified) {
    emit('authenticated')

    return true
  }

  if (!isEmailVerified) {
    currentView.value = 'verifyEmail'
    waitingForEmailVerification.value = true

    try {
      isEmailVerified = await new Promise(
        (resolve) => {
          const { pause } = useIntervalFn(
            async () => {
              try {
                const { data: userFetched } = await api.user.fetch()

                if (userFetched && userFetched.emailVerified) {
                // Update user
                  userStore.user = userFetched

                  // Refresh teams too as credits might have changed
                  await useTeamsStore().fetchTeams()

                  // Resolve
                  resolve(true)
                  pause()
                }

                tries++

                if (tries >= 99) {
                  resolve(false)
                  pause()
                }
              }
              catch (error) {
                console.error(error)
              }
            },
            3000,
          )
        },
      )
    }
    catch {}

    waitingForEmailVerification.value = false
  }

  // Set verified view until the modal gets closed
  if (isEmailVerified) {
    currentView.value = 'emailVerified'
  }

  emit('authenticated')

  return isEmailVerified
}

async function onAuthenticated() {
  if (props.waitForEmailVerification) {
    await waitForEmailVerification()
  }
  else {
    emit('authenticated')
  }

  if (String(route.name).startsWith('auth-login')) {
    router.push(localePath('companies'))
  }
}

async function onLogin() {
  if (!showPasswordField.value) {
    showPasswordField.value = true

    return
  }

  if (!loggingInWithEmail.value) {
    try {
      loggingInWithEmail.value = true

      form.recaptchaToken = await getRecaptchaToken()

      const { isNew, user } = await authStore.loginWithEmail({
        email: form.email,
        invitationToken: route?.query?.invitationToken as string,
        locale: locale.value as Locale,
        password: form.password,
        recaptchaToken: form.recaptchaToken,
      })

      createToast({
        i18nParams: { fullName: user.fullName || '' },
        message: `authWelcome${user.fullName ? 'FullName' : ''}`,
      })

      createEvent({
        name: `[Auth] ${isNew ? 'Register' : 'Login'}`,
        properties: { Platform: 'email' },
      })

      resetRecaptcha()

      await onAuthenticated()
    }
    catch (error) {
      console.error(error)
    }
  }

  loggingInWithEmail.value = false
}

async function onForgotPassword() {
  if (!loggingInWithEmail.value) {
    try {
      loggingInWithEmail.value = true

      await authStore.forgotPassword({ email: form.email })

      currentView.value = 'login'

      createToast({ message: 'emailSent' })
      createEvent({ name: '[Auth] Forgot Password' })
    }
    catch (error) {
      console.error(error)
    }
  }

  loggingInWithEmail.value = false
}

function onSignedInWithGoogle() {
  emit('authenticated')
}

function onUpdateCurrentView(newCurrentView: AuthBoxCurrentView) {
  currentView.value = newCurrentView
  loggingInWithEmail.value = false
}

function onOpenGmail() {
  window.open('https://mail.google.com/mail/', '_blank')
}

async function onResendVerificationEmail() {
  resending.value = true

  try {
    const message = await authStore.resendVerificationEmail({ email: userStore.user?.email || '' })

    if (message === 'emailSent') {
      createToast({ message: 'authVerificationEmailSent' })
      createEvent({ name: '[Auth] Resend Verification Email' })
    }
    else if (message === 'emailAlreadySent') {
      createToast({
        message: 'authVerificationEmailAlreadySent',
        status: 'error',
      })
    }
  }
  catch (error) {
    console.error(error)
  }

  resending.value = false
}
</script>

<template>
  <div class="mx-auto w-full max-w-lg select-none">
    <template v-if="currentView === 'login'">
      <AuthGoogleConnect
        block
        :size="size"
        @authenticated="onSignedInWithGoogle"
      />

      <UDivider
        :class="{
          'my-5': size === 'sm',
          'my-6': size === 'lg',
        }"
        :label="t('orWithYourProfessionalEmail')"
        :ui="{ label: size === 'sm' ? 'text-sm' : 'text-base' }"
      />

      <UForm
        :class="{
          'space-y-4': size === 'sm',
          'space-y-5': size === 'lg',
        }"
        :schema="validation"
        :state="form"
        :validate-on="['submit']"
        @submit="onLogin"
      >
        <UFormGroup name="email">
          <UInput
            v-model="form.email"
            autocomplete="email"
            :disabled="!!route?.query?.email"
            placeholder="name@company.com"
            :size="size === 'sm' ? 'lg' : 'xl'"
          />
        </UFormGroup>

        <template v-if="showPasswordField">
          <UFormGroup name="password">
            <UInput
              v-model="form.password"
              autocomplete="current-password"
              autofocus
              :placeholder="t('enterYouPassword')"
              :size="size === 'sm' ? 'lg' : 'xl'"
              type="password"
            />
          </UFormGroup>

          <span
            class="mt-1 flex cursor-pointer select-none items-start hover:underline dark:text-gray-200"
            :class="{
              'text-sm': size === 'sm',
              'text-base': size === 'lg',
            }"
            @click="onUpdateCurrentView('forgotPassword')"
          >
            <UIcon
              class="mt-0.75 flex-shrink-0"
              :class="{
                'mr-1.5 size-3.5': size === 'sm',
                'mr-2 size-5': size === 'lg',
              }"
              :name="getIcon('password')"
            />

            {{ t('forgotPassword?') }}
          </span>
        </template>

        <UButton
          block
          :icon="getIcon('login')"
          :label="t('continue')"
          :loading="loggingInWithEmail"
          :size="size === 'sm' ? 'lg' : 'xl'"
          type="submit"
          :ui="{ icon: { base: 'text-indigo-600 dark:text-indigo-400' } }"
        />
      </UForm>
    </template>

    <template v-else-if="currentView === 'forgotPassword'">
      <UForm
        :class="{
          'space-y-4': size === 'sm',
          'space-y-5': size === 'lg',
        }"
        :schema="validation"
        :state="form"
        @submit="onForgotPassword"
      >
        <UFormGroup name="email">
          <UInput
            v-model="form.email"
            autocomplete="email"
            autofocus
            placeholder="name@company.com"
            :size="size === 'sm' ? 'lg' : 'xl'"
          />
        </UFormGroup>

        <span
          class="mt-1 flex cursor-pointer select-none items-start hover:underline  dark:text-gray-200"
          :class="{
            'text-sm': size === 'sm',
            'text-base': size === 'lg',
          }"
          @click="onUpdateCurrentView('login')"
        >
          <UIcon
            class="mt-1 flex-shrink-0"
            :class="{
              'mr-1.5 size-3.5': size === 'sm',
              'mr-2 size-5': size === 'lg',
            }"
            :name="getIcon('back')"
          />

          {{ t('backToLogin?') }}
        </span>

        <UButton
          block
          :icon="getIcon('mailbox')"
          :label="t('resetPassword')"
          :loading="loggingInWithEmail"
          :size="size === 'sm' ? 'lg' : 'xl'"
          type="submit"
          :ui="{ icon: { base: 'text-indigo-600 dark:text-indigo-400' } }"
        />
      </UForm>
    </template>

    <div
      v-if="currentView === 'verifyEmail'"
      class="mt-4 flex items-center justify-center text-sm text-gray-500"
    >
      <div class="flex flex-col items-center justify-center gap-4">
        <AnimateRadiantText class="text-lg">
          {{ t('waitingForEmailVerification') }}
        </AnimateRadiantText>

        <UDivider class="w-full overflow-visible">
          <BaseEmblem />
        </UDivider>

        <div class="grid grid-cols-3 gap-2">
          <UButton
            v-for="action in verifyEmailActions"
            :key="action.label"
            block
            :icon="action.icon"
            :label="action.label"
            :loading="action.loading"
            :ui="{ icon: { base: 'text-indigo-600 dark:text-indigo-400' } }"
            variant="solid"
            @click="action.click"
          />
        </div>
      </div>
    </div>

    <div
      v-if="currentView === 'emailVerified'"
      class="mt-4 flex items-center justify-center text-sm text-gray-500"
    >
      <div class="flex flex-col items-center justify-center gap-2">
        {{ t('emailVerified') }}

        <UIcon
          class="size-8"
          :name="getIcon('check')"
        />
      </div>
    </div>

    <div ref="recaptchaRef" />
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "backToLogin?": "Back to login?",
    "continue": "Continue",
    "emailVerified": "Email verified",
    "enterYouPassword": "Enter your password",
    "forgotPassword?": "Forgot password?",
    "logout": "Logout",
    "openGmail": "Open Gmail",
    "orWithYourProfessionalEmail": "or with your professional email",
    "resendEmail": "Resend email",
    "resetPassword": "Reset password",
    "waitingForEmailVerification": "Waiting for email verification..."
  },
  "es": {
    "backToLogin?": "Volver a la página de inicio",
    "continue": "Continuar",
    "emailVerified": "Email verificado",
    "enterYouPassword": "Introduce tu contraseña",
    "forgotPassword?": "¿Olvidaste tu contraseña?",
    "logout": "Cerrar sesión",
    "openGmail": "Abrir Gmail",
    "orWithYourProfessionalEmail": "o con tu email profesional",
    "resendEmail": "Reenviar email",
    "resetPassword": "Restablecer contraseña",
    "waitingForEmailVerification": "Esperando verificación de email..."
  },
  "fr": {
    "backToLogin?": "Se connecter ?",
    "continue": "Continuer",
    "emailVerified": "Email vérifié",
    "enterYouPassword": "Entrez votre mot de passe",
    "forgotPassword?": "Mot de passe oublié ?",
    "logout": "Se déconnecter",
    "openGmail": "Ouvrir Gmail",
    "orWithYourProfessionalEmail": "ou avec votre email professionnel",
    "resendEmail": "Renvoyer l'email",
    "resetPassword": "Réinitialiser mot de passe",
    "waitingForEmailVerification": "En attente de vérification de l'email..."
  }
}
</i18n>

<style>
.grecaptcha-badge {
  visibility: hidden;
}
</style>
